package com.evoapps.evoshift.review

import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.domain.repository.AuthRepository
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.ReviewPromptRepository
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.review.ReviewPromptDecision
import com.evoapps.evoshift.domain.review.ReviewPromptInput
import com.evoapps.evoshift.domain.review.ReviewPromptPolicy
import com.evoapps.evoshift.domain.review.SuccessMoment
import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.settings.LocalSettings
import kotlin.time.Clock
import kotlin.time.Duration
import kotlin.time.Duration.Companion.seconds
import kotlin.time.Instant
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.toLocalDateTime

/**
 * The platform's store rating dialog (SH-240): Play In-App Review on Android, StoreKit on iOS;
 * none on the web and the desktop. The store decides whether it really shows (it has its own
 * quotas) and never tells the app whether or what the person rated.
 */
interface StoreReviewLauncher {
    val isSupported: Boolean

    /** The app is in front of the person right now (a visible, resumed screen). */
    fun isInForeground(): Boolean

    /** Asks the store for its dialog; false when it could not be asked. */
    suspend fun launch(): Boolean

    object None : StoreReviewLauncher {
        override val isSupported = false
        override fun isInForeground() = false
        override suspend fun launch() = false
    }
}

expect fun platformStoreReviewLauncher(): StoreReviewLauncher

/**
 * Success moments reported by the view models (like AppTelemetry): [ReviewPrompter.start]
 * installs the handler when the app's UI starts; before that (and in tests) they go nowhere.
 */
object AppReviewPrompt {
    private var handler: (SuccessMoment, String) -> Unit = { _, _ -> }

    fun install(handler: (SuccessMoment, String) -> Unit) {
        this.handler = handler
    }

    /** [moment] finished successfully at [workplaceId]. */
    fun onSuccess(moment: SuccessMoment, workplaceId: String) = handler(moment, workplaceId)
}

/**
 * Asks for a store rating at a success moment when [ReviewPromptPolicy] allows it (SH-240): reads
 * the person's role and their workplace's published rosters and days with takings only after the
 * cheap checks passed (and only on Android and iOS), waits [settleDelay] so the success message is
 * seen first, and asks only while the app is still in the foreground. The date is kept on the device
 * and in users/{uid}/settings/review before the store is asked, so neither a crash nor another
 * device asks again within 90 days.
 */
class ReviewPrompter(
    private val settings: LocalSettings,
    private val repository: ReviewPromptRepository,
    private val authRepository: AuthRepository,
    private val rosterRepository: RosterRepository,
    private val financialRepository: FinancialRepository,
    private val launcher: StoreReviewLauncher,
    private val scope: CoroutineScope,
    private val clock: Clock = AppClock,
    private val zone: () -> TimeZone = { TimeZone.currentSystemDefault() },
    private val settleDelay: Duration = 1.5.seconds
) {
    private var started = false
    private var firstLaunch = true
    private var running: Job? = null

    /** The app's UI started: counts this start (the first one never asks) and listens for moments. */
    fun start() {
        if (started) return
        started = true
        val launches = settings.getString(KEY_LAUNCHES)?.toIntOrNull() ?: 0
        firstLaunch = launches == 0
        settings.putString(KEY_LAUNCHES, (launches + 1).coerceAtMost(MAX_COUNTED_LAUNCHES).toString())
        AppReviewPrompt.install(::onSuccess)
    }

    fun onSuccess(moment: SuccessMoment, workplaceId: String) {
        if (!launcher.isSupported || running?.isActive == true) return
        running = scope.launch { runCatching { evaluate(moment, workplaceId) } }
    }

    /** The decision for one moment (and the prompt when it is [ReviewPromptDecision.ASK]); null: not asked. */
    internal suspend fun evaluate(moment: SuccessMoment, workplaceId: String): ReviewPromptDecision? {
        if (!launcher.isSupported) return null
        val user = authRepository.observeCurrentUser().first() ?: return null
        val engagement = user.engagements[workplaceId]
        val now = clock.now()
        val local = settings.getString(KEY_LAST_PROMPT)?.let { runCatching { Instant.parse(it) }.getOrNull() }
        fun input(last: Instant?, rosters: Int, takingsDays: Int) = ReviewPromptInput(
            moment = moment, now = now, permission = engagement?.permission, engagementStart = engagement?.startDate,
            isFirstLaunch = firstLaunch, lastPromptAt = last, publishedRosters = rosters, takingsDays = takingsDays,
            zone = zone()
        )
        // Everything but the counts first: most moments stop here without a read.
        val cheap = ReviewPromptPolicy.decide(input(local, Int.MAX_VALUE, Int.MAX_VALUE))
        if (cheap != ReviewPromptDecision.ASK) return cheap
        val last = listOfNotNull(local, repository.lastPromptAt(user.uid)).maxOrNull()
        val decision = ReviewPromptPolicy.decide(input(last, publishedRosters(workplaceId, moment), takingsDays(workplaceId, moment)))
        if (decision != ReviewPromptDecision.ASK) return decision
        delay(settleDelay)
        if (!launcher.isInForeground()) return null
        settings.putString(KEY_LAST_PROMPT, now.toString())
        repository.savePromptAt(user.uid, now)
        launcher.launch()
        return decision
    }

    private suspend fun publishedRosters(workplaceId: String, moment: SuccessMoment): Int {
        if (moment != SuccessMoment.ROSTER_PUBLISHED) return 0
        return rosterRepository.observeRosters(workplaceId).first().count { it.status in PUBLISHED }
    }

    /** Days with takings in the last [TAKINGS_WINDOW_DAYS] days (at most that many records read). */
    private suspend fun takingsDays(workplaceId: String, moment: SuccessMoment): Int {
        if (moment != SuccessMoment.TAKINGS_ENTERED) return 0
        val today = clock.now().toLocalDateTime(zone()).date
        val from = today.minus(DatePeriod(days = TAKINGS_WINDOW_DAYS - 1))
        return financialRepository.getDailyRecordsInRange(workplaceId, from.toString(), today.toString())
            .getOrNull().orEmpty()
            .filter { it.revenue.realNetto != null }
            .distinctBy { it.date }
            .size
    }

    companion object {
        const val KEY_LAUNCHES = "reviewPrompt.launches"
        const val KEY_LAST_PROMPT = "reviewPrompt.lastAt"
        const val TAKINGS_WINDOW_DAYS = 60
        private const val MAX_COUNTED_LAUNCHES = 1000
        private val PUBLISHED = setOf(RosterStatus.PUBLISHED, RosterStatus.LOCKED, RosterStatus.ARCHIVED)
    }
}
